// client/src/components/MissionControlBackdrop.tsx
// "Mission control" hero backdrop: dial gauges, level bars,
// status LEDs and a T-minus deadline readout. Decorative only — the numbers are not live data.
// Runs for `runForMs`, fades out over `fadeMs`, then unmounts so nothing keeps animating.
// Keyframes live in index.css (.mc-*).

import React, { useEffect, useState } from 'react';

// Next-deadline readout. Dates are local calendar dates (YYYY-MM-DD).
const DEADLINES: { date: string; label: string }[] = [
  { date: '2026-10-15', label: 'EXTENSION DEADLINE' },
  { date: '2027-03-15', label: 'S-CORP / PARTNERSHIP DEADLINE' },
  { date: '2027-04-15', label: 'FILING DEADLINE' },
  { date: '2027-09-15', label: 'S-CORP / PARTNERSHIP EXT. DEADLINE' },
  { date: '2027-10-15', label: 'EXTENSION DEADLINE' },
];

function nextDeadline(): { days: number; label: string } | null {
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  for (const d of DEADLINES) {
    const [y, m, day] = d.date.split('-').map(Number);
    const days = Math.round((Date.UTC(y, m - 1, day) - today) / 86_400_000);
    if (days >= 0) return { days, label: d.label };
  }
  return null;
}

const CIRC = 2 * Math.PI * 20; // ring gauge circumference, r = 20

function Gauge({ a, b, secs, delay = 0 }: { a: number; b: number; secs: number; delay?: number }) {
  // a/b are the min/max fill (0–1) of a 270° arc the needle eases between.
  const arc = CIRC * 0.75;
  const off = (v: number) => arc * (1 - v);
  return (
    <svg width="58" height="58" viewBox="0 0 48 48" aria-hidden="true">
      <g transform="rotate(135 24 24)" fill="none" strokeWidth="3" strokeLinecap="round">
        <circle className="mc-track" cx="24" cy="24" r="20" strokeDasharray={`${arc} ${CIRC}`} />
        <circle
          className="mc-anim mc-gauge mc-arc"
          cx="24" cy="24" r="20"
          strokeDasharray={`${arc} ${CIRC}`}
          style={{
            ['--a' as string]: off(a),
            ['--b' as string]: off(b),
            animationDuration: `${secs}s`,
            animationDelay: `${delay}s`,
          }}
        />
      </g>
      <circle className="mc-dot" cx="24" cy="24" r="1.8" />
    </svg>
  );
}

export default function MissionControlBackdrop({
  runForMs = 30000,
  fadeMs = 2500,
}: { runForMs?: number; fadeMs?: number }) {
  const [phase, setPhase] = useState<'on' | 'fading' | 'done'>('on');
  const [deadline] = useState(nextDeadline);

  useEffect(() => {
    const t1 = window.setTimeout(() => setPhase('fading'), runForMs);
    const t2 = window.setTimeout(() => setPhase('done'), runForMs + fadeMs);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [runForMs, fadeMs]);

  if (phase === 'done') return null;

  return (
    <div className="mc-backdrop" aria-hidden="true">
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: phase === 'on' ? 1 : 0,
          transition: `opacity ${fadeMs}ms ease-out`,
        }}
      >
        {/* Status LEDs */}
        <div className="mc-side" style={{ position: 'absolute', top: 18, left: 22, display: 'flex', gap: 7 }}>
          {[0, 0.7, 1.4, 2.1].map(d => (
            <span key={d} className="mc-anim mc-led" style={{ animationDelay: `${d}s` }} />
          ))}
        </div>

        {/* Deadline readout */}
        {deadline && (
          <div className="mc-side mc-readout" style={{ position: 'absolute', top: 14, right: 22 }}>
            <div className="mc-anim mc-tminus">
              T−{deadline.days} {deadline.days === 1 ? 'DAY' : 'DAYS'}
            </div>
            <div className="mc-label">{deadline.label}</div>
          </div>
        )}

        {/* Gauges */}
        <div className="mc-side" style={{ position: 'absolute', left: 28, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Gauge a={0.35} b={0.8} secs={7} />
          <Gauge a={0.5} b={0.92} secs={5} delay={-2} />
        </div>

        {/* Level bars */}
        <div className="mc-side" style={{ position: 'absolute', right: 30, bottom: 26, display: 'flex', alignItems: 'flex-end', gap: 5, height: 56 }}>
          {[0, 0.4, 0.9, 0.2, 0.7, 1.1, 0.5, 0.3].map((d, i) => (
            <span key={i} className="mc-anim mc-bar" style={{ animationDelay: `${-d}s`, animationDuration: `${1.6 + (i % 3) * 0.5}s` }} />
          ))}
        </div>
      </div>
    </div>
  );
}
